import { useMemo } from 'react';
import {
  Text,
  Stack,
  Box,
  Group,
  ActionIcon,
  SimpleGrid,
  TextInput,
  NumberInput,
  UnstyledButton,
  Tooltip,
  Alert,
  Divider,
  Select,
  Card,
  Button,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { Plus, Trash2, AlertTriangle, ArrowRight, Clock, Info } from 'react-feather';
import type { DatesStepProps } from './types';
import type { ModalityType, DayOfWeek } from '../../../types';
import { MODALITY_LABELS } from '../../../config/cohortConfig';
import classes from './CreateCohortPage.module.css';
import { getTimezoneAbbreviation } from '../../../utils/timezone';
import { TimezoneSelect } from '../../../components/common';
import { toDateValue, generateAnchoredSessionDates } from '../../../utils/dateHelpers';
import { DateTimeField, TimeField } from '../../../components/common';
import { DEFAULT_START_TIME_ON_DATE_PICK } from '../../../utils/sessionEndTime';
import type { CheckinSettingsForm } from '../components/CheckinSettingsFields';
import { deriveCohortDateRange, wizardProgramWeeks } from './cohortDateRange';
import { checkinWeekText } from '../../../utils/checkinWeeks';

// Days of week options for select
const DAY_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
];

// Get display text for day of week
const getDayLabel = (day: DayOfWeek) => {
  return DAY_OPTIONS.find(d => d.value === day)?.label || day;
};

// Get timezone abbreviation (delegates to central utility)
const getTimezoneAbbr = (tz: string) => {
  return getTimezoneAbbreviation(tz);
};

// Format time for display (HH:MM -> h:mm AM/PM)
const formatTimeDisplay = (time: string) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${ampm}`;
};

export function DatesStep({
  form,
  sessionConflicts,
  onAddConstraint,
  onRemoveConstraint,
  onAddCoachHuddle,
  onRemoveCoachHuddle,
  onAddTimeSlot,
  onRemoveTimeSlot,
  onUpdateTimeSlot,
  onCheckinsChange,
}: DatesStepProps) {
  const { selectedModalities, groupCoachingConfig, oneOnOneConfig, workshopConfig, sessionDates, timeSlots = [] } = form.values;

  // Check if any session dates exceed the cohort wrap date
  const wrapDateConflict = useMemo(() => {
    const wrapDate = form.values.cohortDates.cohortWrapUp;
    if (!wrapDate) return null;

    let latestSessionDate: Date | null = null;
    const modalities: ModalityType[] = ['group_coaching', 'one_on_one', 'workshop'];

    for (const modality of modalities) {
      if (!selectedModalities.includes(modality)) continue;
      for (const date of sessionDates[modality]) {
        if (date && (!latestSessionDate || date > latestSessionDate)) {
          latestSessionDate = date;
        }
      }
    }

    if (!latestSessionDate || latestSessionDate <= wrapDate) return null;

    const suggestedDate = new Date(latestSessionDate);
    suggestedDate.setDate(suggestedDate.getDate() + 7);

    const formatDate = (d: Date) =>
      d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    return {
      wrapDateFormatted: formatDate(wrapDate),
      suggestedDateFormatted: formatDate(suggestedDate),
    };
  }, [form.values.cohortDates.cohortWrapUp, sessionDates, selectedModalities]);

  // Get session count and frequency for a modality
  const getModalityConfig = (modality: ModalityType) => {
    switch (modality) {
      case 'group_coaching':
        return { count: groupCoachingConfig.sessionCount, frequency: groupCoachingConfig.frequency };
      case 'one_on_one':
        return { count: oneOnOneConfig.sessionCount, frequency: oneOnOneConfig.frequency };
      case 'workshop':
        return { count: workshopConfig.workshops.length, frequency: null };
    }
  };

  // Check if Week 1 date is set for a modality
  const hasWeekOneDate = (modality: ModalityType): boolean => {
    const dates = sessionDates[modality];
    return dates.length > 0 && dates[0] !== null;
  };

  // Populate dates for a modality based on Week 1 (anchor) date and cadence.
  // Explicit user action — overwrites weeks 2..N from the anchor.
  const populateDates = (modality: ModalityType) => {
    const config = getModalityConfig(modality);
    const dates = sessionDates[modality];
    const week1Date = dates[0];

    if (!week1Date || config.count <= 1) return;

    const schedule = generateAnchoredSessionDates(week1Date, config.count, config.frequency);
    const newDates = [...dates];
    for (let i = 1; i < config.count; i++) {
      newDates[i] = schedule[i];
    }

    const updatedSessionDates = { ...sessionDates };
    updatedSessionDates[modality] = newDates;
    form.setFieldValue('sessionDates', updatedSessionDates);
  };

  // Check if populated dates would have conflicts
  const wouldHaveConflicts = (modality: ModalityType): boolean => {
    const config = getModalityConfig(modality);
    const dates = sessionDates[modality];
    const week1Date = dates[0];

    if (!week1Date || config.count <= 1) return false;

    const schedule = generateAnchoredSessionDates(week1Date, config.count, config.frequency);

    // Check if any projected dates fall in constraints
    for (let i = 1; i < config.count; i++) {
      for (const constraint of form.values.dateConstraints) {
        if (constraint.start_date && constraint.end_date) {
          if (schedule[i] >= constraint.start_date && schedule[i] <= constraint.end_date) {
            return true;
          }
        }
      }
    }

    return false;
  };

  // Get session labels for a modality
  const getSessionLabels = (modality: ModalityType, count: number) => {
    if (modality === 'workshop') {
      return workshopConfig.workshops.map((w, i) => w.title || `Workshop ${i + 1}`);
    }
    return Array.from({ length: count }, (_, i) => `Week ${i + 1}`);
  };

  // Update session date for a modality
  const updateSessionDate = (modality: ModalityType, index: number, value: Date | null) => {
    const newDates = { ...sessionDates };
    const modalityDates = [...newDates[modality]];
    modalityDates[index] = value;
    newDates[modality] = modalityDates;
    form.setFieldValue('sessionDates', newDates);
  };

  // MED-1350: the target week of each check-in that is on. The switches are
  // in the Session Configuration step; the weeks are set here, beside the
  // other session dates. Fixed order (Intro, then Midpoint) so a field does
  // not move while the CM types in it.
  const hasCheckinHost =
    selectedModalities.includes('group_coaching') || selectedModalities.includes('one_on_one');
  const { checkins } = form.values;
  const enabledCheckins = [
    {
      key: 'intro',
      label: 'Intro',
      week: checkins.introWeek,
      enabled: checkins.introEnabled,
    },
    {
      key: 'midpoint',
      label: 'Midpoint',
      week: checkins.midpointWeek,
      enabled: checkins.midpointEnabled,
    },
  ].filter(c => c.enabled);
  const checkinError =
    typeof form.errors.checkins === 'string' ? form.errors.checkins : null;
  const checkinProgramWeeks = wizardProgramWeeks(form.values);
  // Week 1 starts on the cohort start date (the first session date).
  const cohortStartDate = deriveCohortDateRange(form.values).startDate;

  const updateCheckinWeek = (key: string, v: string | number) => {
    const week = typeof v === 'number' ? v : null;
    const next: CheckinSettingsForm =
      key === 'intro'
        ? { ...checkins, introWeek: week }
        : { ...checkins, midpointWeek: week };
    onCheckinsChange(next);
  };


  const renderCheckins = () => (
    <>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        {enabledCheckins.map(c => (
          <NumberInput
            key={c.key}
            label={c.label}
            aria-label={`${c.label} check-in target week`}
            placeholder="Target week"
            min={1}
            allowDecimal={false}
            allowNegative={false}
            size="sm"
            value={c.week ?? ''}
            onChange={v => updateCheckinWeek(c.key, v)}
            description={
              checkinWeekText(c.week, cohortStartDate) ??
              (c.key === 'midpoint' && checkinProgramWeeks === null
                ? // MED-1350: the wizard fills the half-way week only once two
                  // different session dates give the program a length.
                  'Fills in at half-way once two different session dates are set.'
                : undefined)
            }
            inputWrapperOrder={['label', 'input', 'description', 'error']}
          />
        ))}
      </SimpleGrid>
      {checkinError && (
        <Text size="sm" c="red" mt="xs" role="alert">
          {checkinError}
        </Text>
      )}
    </>
  );

  // Render session dates for a modality
  const renderModalitySessions = (modality: ModalityType) => {
    const config = getModalityConfig(modality);
    const labels = getSessionLabels(modality, config.count);
    const dates = sessionDates[modality];
    const conflicts = sessionConflicts[modality] || [];

    if (config.count === 0) {
      return (
        <Text size="sm" c="dimmed">
          {modality === 'workshop'
            ? 'Add workshops in the Session Configuration step first.'
            : 'No sessions configured.'}
        </Text>
      );
    }

    return (
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        {labels.map((label, index) => (
          <DateInput
            key={index}
            label={label}
            placeholder="Select date"
            valueFormat="MM/DD/YYYY"
            value={dates[index] || null}
            onChange={(value) => updateSessionDate(modality, index, toDateValue(value))}
            clearable
            size="sm"
            popoverProps={{ withinPortal: true }}
            error={conflicts[index] ? 'Falls within a constraint period' : undefined}
          />
        ))}
      </SimpleGrid>
    );
  };

  return (
    <Stack gap="lg">
      <Box>
        <Text size="lg" fw={600}>Dates</Text>
        <Text size="sm" c="dimmed" mt="xs">
          Set date constraints and session dates for your cohort.
        </Text>
      </Box>

      {/* Date Constraints */}
      <Box className={classes.wizardSection}>
        <Text size="sm" fw={600} mb="md">Date Constraints</Text>

        <Stack gap="sm">
          {form.values.dateConstraints.map((constraint, index) => (
            <Box
              key={index}
              p="sm"
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #E8E6E3',
              }}
            >
              <Group wrap="nowrap" gap="sm">
                <DateInput
                  placeholder="Start Date"
                  valueFormat="MM/DD/YYYY"
                  value={constraint.start_date}
                  onChange={(value) => {
                    const newConstraints = [...form.values.dateConstraints];
                    newConstraints[index] = {
                      ...newConstraints[index],
                      start_date: toDateValue(value),
                    };
                    form.setFieldValue('dateConstraints', newConstraints);
                  }}
                  style={{ flex: 1 }}
                  size="sm"
                  clearable
                  popoverProps={{ withinPortal: true }}
                />
                <DateInput
                  placeholder="End Date"
                  valueFormat="MM/DD/YYYY"
                  value={constraint.end_date}
                  onChange={(value) => {
                    const newConstraints = [...form.values.dateConstraints];
                    newConstraints[index] = {
                      ...newConstraints[index],
                      end_date: toDateValue(value),
                    };
                    form.setFieldValue('dateConstraints', newConstraints);
                  }}
                  style={{ flex: 1 }}
                  size="sm"
                  clearable
                  popoverProps={{ withinPortal: true }}
                />
                <TextInput
                  placeholder="Label (optional)"
                  value={constraint.label || ''}
                  onChange={(e) => {
                    const newConstraints = [...form.values.dateConstraints];
                    newConstraints[index] = {
                      ...newConstraints[index],
                      label: e.currentTarget.value,
                    };
                    form.setFieldValue('dateConstraints', newConstraints);
                  }}
                  style={{ flex: 1 }}
                  size="sm"
                />
                <ActionIcon color="red" variant="subtle" onClick={() => onRemoveConstraint(index)}>
                  <Trash2 size={16} />
                </ActionIcon>
              </Group>
            </Box>
          ))}

          <UnstyledButton
            onClick={onAddConstraint}
            p="sm"
            style={{
              borderRadius: '8px',
              border: '2px dashed #D0CEC9',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              color: 'var(--mantine-color-gray-6)',
              width: '100%',
            }}
          >
            <Plus size={16} />
            <Text size="sm" fw={500}>Add Date Constraint</Text>
          </UnstyledButton>
        </Stack>
      </Box>

      {/* Cohort Dates */}
      <Box className={classes.wizardSection}>
        <Text size="sm" fw={600} mb="md">Cohort Dates</Text>

        <Stack gap="md">
          {/* Coach Immersion */}
          <Box>
            <DateTimeField
              defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
              label="Coach Immersion"
              value={form.values.cohortDates.coachImmersion}
              onChange={(value) => {
                form.setFieldValue('cohortDates', {
                  ...form.values.cohortDates,
                  coachImmersion: value,
                });
              }}
              size="sm"
            />
          </Box>

          {/* Participant Orientation */}
          <Box>
            <DateTimeField
              defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
              label="Participant Orientation"
              value={form.values.cohortDates.participantOrientation}
              onChange={(value) => {
                form.setFieldValue('cohortDates', {
                  ...form.values.cohortDates,
                  participantOrientation: value,
                });
              }}
              size="sm"
            />
          </Box>

          {/* Coach Huddles. MED-1350: these are coach huddle cohort events,
              not the intro or midpoint check-ins, so the copy says huddles. */}
          <Box>
            <Text size="sm" fw={500} mb="xs">Coach huddles</Text>
            <Stack gap="sm">
              {form.values.cohortDates.coachHuddles.map((huddle, index) => (
                <Group key={index} gap="sm" align="flex-end">
                  <DateTimeField
                    defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
                    value={huddle}
                    onChange={(value) => {
                      const newHuddles = [...form.values.cohortDates.coachHuddles];
                      newHuddles[index] = value;
                      form.setFieldValue('cohortDates', {
                        ...form.values.cohortDates,
                        coachHuddles: newHuddles,
                      });
                    }}
                    size="sm"
                    style={{ flex: 1 }}
                  />
                  <ActionIcon color="red" variant="subtle" onClick={() => onRemoveCoachHuddle(index)}>
                    <Trash2 size={16} />
                  </ActionIcon>
                </Group>
              ))}
              <UnstyledButton
                onClick={onAddCoachHuddle}
                p="xs"
                style={{
                  borderRadius: '6px',
                  border: '2px dashed #D0CEC9',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  color: 'var(--mantine-color-gray-6)',
                  width: 'fit-content',
                }}
              >
                <Plus size={14} />
                <Text size="xs" fw={500}>Add coach huddle</Text>
              </UnstyledButton>
            </Stack>
          </Box>

          {/* Cohort Wrap Up */}
          <Box>
            <Text size="sm" fw={500} mb="xs">
              Cohort Wrap Up
            </Text>
            <DateInput
              placeholder="mm/dd/yyyy"
              value={form.values.cohortDates.cohortWrapUp}
              onChange={(value) => {
                form.setFieldValue('cohortDates', {
                  ...form.values.cohortDates,
                  cohortWrapUp: toDateValue(value),
                });
                if (value) {
                  form.clearFieldError('cohortWrapUp');
                }
              }}
              valueFormat="MMMM D, YYYY"
              size="sm"
              clearable
              error={form.errors.cohortWrapUp}
            />
            {wrapDateConflict && (
              <Alert
                icon={<AlertTriangle size={16} />}
                color="red"
                variant="light"
                mt="sm"
              >
                <Text size="sm">
                  Session dates extend past the cohort wrap date ({wrapDateConflict.wrapDateFormatted}).
                  Please update the cohort wrap date to <Text span fw={600}>{wrapDateConflict.suggestedDateFormatted}</Text> or later to accommodate all sessions.
                </Text>
              </Alert>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Session Week Dates */}
      {selectedModalities.length > 0 && (
        <Box>
          <Text size="sm" fw={600} mb="md">
            Session Week Dates <Text span c="dimmed" fw={400}>(optional - actual session dates will vary by group)</Text>
          </Text>

          <Stack gap="md">
            {selectedModalities.includes('group_coaching') && (
              <Box className={classes.wizardSection}>
                <Group justify="space-between" mb="sm">
                  <Group gap="xs">
                    <Text size="sm" fw={500}>{MODALITY_LABELS.group_coaching}</Text>
                    <Text size="xs" c="dimmed">
                      ({groupCoachingConfig.sessionCount} sessions)
                    </Text>
                  </Group>
                  {hasWeekOneDate('group_coaching') && (
                    <UnstyledButton
                      onClick={() => populateDates('group_coaching')}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      {wouldHaveConflicts('group_coaching') && (
                        <Tooltip label="Some dates may conflict with constraints" withArrow>
                          <AlertTriangle size={14} color="#E9A23B" />
                        </Tooltip>
                      )}
                      <Text size="sm" c="dimmed" style={{ cursor: 'pointer' }}>
                        Populate dates
                      </Text>
                      <ArrowRight size={14} color="var(--mantine-color-gray-6)" />
                    </UnstyledButton>
                  )}
                </Group>
                {renderModalitySessions('group_coaching')}
              </Box>
            )}

            {selectedModalities.includes('one_on_one') && (
              <Box className={classes.wizardSection}>
                <Group justify="space-between" mb="sm">
                  <Group gap="xs">
                    <Text size="sm" fw={500}>{MODALITY_LABELS.one_on_one}</Text>
                    <Text size="xs" c="dimmed">
                      ({oneOnOneConfig.sessionCount} sessions)
                    </Text>
                  </Group>
                  {hasWeekOneDate('one_on_one') && (
                    <UnstyledButton
                      onClick={() => populateDates('one_on_one')}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      {wouldHaveConflicts('one_on_one') && (
                        <Tooltip label="Some dates may conflict with constraints" withArrow>
                          <AlertTriangle size={14} color="#E9A23B" />
                        </Tooltip>
                      )}
                      <Text size="sm" c="dimmed" style={{ cursor: 'pointer' }}>
                        Populate dates
                      </Text>
                      <ArrowRight size={14} color="var(--mantine-color-gray-6)" />
                    </UnstyledButton>
                  )}
                </Group>
                {renderModalitySessions('one_on_one')}
                {enabledCheckins.length > 0 && (
                  <Box mt="md">
                    <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb="xs" style={{ letterSpacing: '0.3px' }}>
                      Check-ins
                    </Text>
                    {renderCheckins()}
                  </Box>
                )}
              </Box>
            )}

            {/* MED-1350: a cohort with no 1:1 coaching lists its check-ins in their own block. */}
            {hasCheckinHost && !selectedModalities.includes('one_on_one') && enabledCheckins.length > 0 && (
              <Box className={classes.wizardSection}>
                <Group gap="xs" mb="sm">
                  <Text size="sm" fw={500}>Check-ins</Text>
                  <Text size="xs" c="dimmed">
                    ({enabledCheckins.length} {enabledCheckins.length === 1 ? 'check-in' : 'check-ins'})
                  </Text>
                </Group>
                {renderCheckins()}
              </Box>
            )}

            {selectedModalities.includes('workshop') && (
              <Box className={classes.wizardSection}>
                <Group gap="xs" mb="sm">
                  <Text size="sm" fw={500}>{MODALITY_LABELS.workshop}</Text>
                  <Text size="xs" c="dimmed">
                    ({workshopConfig.workshops.length} workshops)
                  </Text>
                </Group>
                {renderModalitySessions('workshop')}
              </Box>
            )}
          </Stack>
        </Box>
      )}

      {/* Session Time Slot Options */}
      {selectedModalities.includes('group_coaching') && (
        <>
          <Divider my="lg" />
          <Box className={classes.wizardSection}>
            <Group gap="xs" mb="sm">
              <Clock size={18} />
              <Text size="sm" fw={600}>Session Time Slot Options</Text>
            </Group>
            <Text size="sm" c="dimmed" mb="md">
              Define available meeting times for group coaching sessions. Participants and coaches will
              indicate their availability from these options.
            </Text>

            {/* Time slots list */}
            {timeSlots.length > 0 ? (
              <Card withBorder p={0} mb="md">
                <Stack gap={0}>
                  {timeSlots.map((slot, index) => (
                    <Box key={slot.id}>
                      <Group justify="space-between" p="sm">
                        <Group gap="xl">
                          <Text size="sm" fw={500} w={80}>
                            Slot {index + 1}
                          </Text>
                          <Text size="sm" w={100}>
                            {getDayLabel(slot.day_of_week)}
                          </Text>
                          <Text size="sm">
                            {formatTimeDisplay(slot.time)} {getTimezoneAbbr(slot.timezone)}
                          </Text>
                        </Group>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => onRemoveTimeSlot(index)}
                        >
                          <Trash2 size={14} />
                        </ActionIcon>
                      </Group>
                      {/* Inline edit form */}
                      <Box px="sm" pb="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                        <Group gap="sm" align="flex-end">
                          <Select
                            label="Day"
                            data={DAY_OPTIONS}
                            value={slot.day_of_week}
                            onChange={(value) =>
                              onUpdateTimeSlot(index, { day_of_week: value as DayOfWeek })
                            }
                            style={{ flex: 1 }}
                            size="sm"
                          />
                          <TimeField
                            label="Time"
                            value={slot.time}
                            onChange={(value) =>
                              onUpdateTimeSlot(index, { time: value })
                            }
                            style={{ flex: 1 }}
                            size="sm"
                          />
                          <TimezoneSelect
                            label="Timezone"
                            value={slot.timezone}
                            onChange={(value) =>
                              onUpdateTimeSlot(index, { timezone: value || 'America/New_York' })
                            }
                            style={{ flex: 1.5 }}
                            size="sm"
                          />
                        </Group>
                      </Box>
                      {index < timeSlots.length - 1 && <Divider />}
                    </Box>
                  ))}
                </Stack>
              </Card>
            ) : (
              <Card withBorder p="xl" mb="md" style={{ borderStyle: 'dashed' }}>
                <Stack align="center" gap="xs">
                  <Button
                    variant="subtle"
                    leftSection={<Plus size={16} />}
                    onClick={onAddTimeSlot}
                  >
                    Add Time Slot
                  </Button>
                </Stack>
              </Card>
            )}

            {/* Add button when slots exist */}
            {timeSlots.length > 0 && (
              <Button
                variant="outline"
                leftSection={<Plus size={16} />}
                onClick={onAddTimeSlot}
                size="sm"
              >
                Add Time Slot
              </Button>
            )}

            {/* Info alert about Paperform numbering */}
            {timeSlots.length > 0 && (
              <Alert icon={<Info size={16} />} color="gray" variant="light" mt="md">
                Number options in Paperform to match this order (
                {timeSlots.map((_, i) => `${i + 1}=Slot ${i + 1}`).join(', ')}).
              </Alert>
            )}
          </Box>
        </>
      )}
    </Stack>
  );
}
